import React, { FunctionComponent } from "react";
import { IChartMergedDatapoint } from "../../../Models/Models";
import { shortenStr } from "../../Common";

import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import { ChartContainer } from "./ChartContainer";

interface IRechartBarLine {
  data?: IChartMergedDatapoint[];
  initialYValue:
    | "nationalWeight"
    | "selection1PrimaryWeight"
    | "selection1SecondaryWeight";
  bar1color: string;
  bar2color?: string;
  xTickFormatter?: (id: string) => string;
  yValueTickFormatter?: (num: number) => string;
  bar1ValueAttr: string;
  bar2ValueAttr?: string;
  verticalXAxis?: boolean;
  chartHeight?: number;
  makeXLabelsFitDemmit?: boolean;
  xAxisHeight?: number;
  selection1Name?: string;
  selection2Name?: string;
  primaryWeightLabel?: string;
  secondaryWeightLabel?: string;
  ignoreXTickFormatterOnTooltip?: boolean;
  nationalAverageLine?: boolean;
  disableAnimation?: boolean;
}

export const RechartBarLine: FunctionComponent<IRechartBarLine> = (
    props: IRechartBarLine
) => {
    const {
        data,
        initialYValue,
        bar1color,
        bar2color,
        xTickFormatter,
        yValueTickFormatter,
        bar1ValueAttr,
        bar2ValueAttr,
        verticalXAxis,
        chartHeight,
        makeXLabelsFitDemmit,
        xAxisHeight,
        ignoreXTickFormatterOnTooltip,
        disableAnimation,
    } = props;

    const animationActive = !disableAnimation;
    const NationalLine: JSX.Element
    = data[0].nationalWeight && props.nationalAverageLine ? (
        <Line
            type="linear"
            dataKey="nationalWeight"
            stroke="#000"
            isAnimationActive={animationActive}
        />
    ) : null;

    const BarSelection2: JSX.Element
    = props.selection2Name?.length > 0 ? (
        <Bar
            dataKey={bar2ValueAttr}
            fill={bar2color}
            isAnimationActive={animationActive}
        />
    ) : null;

    const formatYLabel = (str: string): string => {
        let yLabel;
        switch (str) {
            case "selection1PrimaryWeight":
                yLabel = props.selection1Name
                    ? props.selection1Name
                    : props.primaryWeightLabel;
                break;
            case "selection1SecondaryWeight":
                yLabel = props.selection1Name
                    ? props.selection1Name
                    : props.secondaryWeightLabel;
                break;
            case "selection2PrimaryWeight":
                yLabel = props.selection2Name
                    ? props.selection2Name
                    : props.primaryWeightLabel;
                break;
            case "selection2SecondaryWeight":
                yLabel = props.selection2Name
                    ? props.selection2Name
                    : props.secondaryWeightLabel;
                break;
            default:
                yLabel = "US National Avg";
                break;
        }
        return shortenStr(yLabel, 20);
    };

    const formatLegendText = (value: string) => {
        return <span style={{ fontSize: "9px" }}>{formatYLabel(value)}</span>;
    };

    const formatTooltipText = (value: number, name: string): [string, string] => {
        return [yValueTickFormatter(value), formatYLabel(name)];
    };

    const CustomizedAxisTick = (props) => {
        const { x, y, stroke, payload } = props;
        const value = xTickFormatter
            ? xTickFormatter(payload.value)
            : payload.value;

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={12}
                    textAnchor="end"
                    fill="#666"
                    transform="rotate(-35)"
                    fontSize={9}
                >
                    {value}
                </text>
            </g>
        );
    };

    return (
        <ChartContainer chartHeight={chartHeight}>
            <ResponsiveContainer>
                <ComposedChart
                    data={data}
                    layout={verticalXAxis ? "vertical" : "horizontal"}
                    barGap={0}
                >
                    <CartesianGrid
                        stroke="lightgray"
                        vertical={verticalXAxis ? true : false}
                        horizontal={!verticalXAxis ? true : false}
                    />
                    <XAxis
                        tickFormatter={verticalXAxis ? yValueTickFormatter : xTickFormatter}
                        dataKey={verticalXAxis ? null : "id"}
                        type={verticalXAxis ? "number" : "category"}
                        interval={makeXLabelsFitDemmit ? 0 : "preserveEnd"}
                        tick={
                            makeXLabelsFitDemmit ? <CustomizedAxisTick /> : { fontSize: 9 }
                        }
                        height={xAxisHeight ? xAxisHeight : 30}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        dataKey={verticalXAxis ? "id" : null}
                        tickFormatter={verticalXAxis ? xTickFormatter : yValueTickFormatter}
                        type={verticalXAxis ? "category" : "number"}
                        tick={{ fontSize: 9 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip
                        formatter={formatTooltipText}
                        labelFormatter={
                            xTickFormatter && !ignoreXTickFormatterOnTooltip
                                ? xTickFormatter
                                : null
                        }
                        isAnimationActive={animationActive}
                    />
                    <Legend
                        formatter={formatLegendText}
                        iconType={"circle"}
                        align={"center"}
                        iconSize={9}
                        margin={{ left: 25 }}
                    />
                    <Bar
                        dataKey={bar1ValueAttr}
                        fill={bar1color}
                        isAnimationActive={animationActive}
                    />
                    {BarSelection2}
                    {NationalLine}
                </ComposedChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
};
